import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { deriveQuizGlpProfile } from "./quiz-glp-profile";

/**
 * GLP-1 QUIZ -- cold-traffic destination for the GLP-1 ad set (Ozempic-face /
 * deflated-arm before-afters + the "Ozempic face isn't random" advertorial).
 *
 * The quiz is the ad set unpacked: each ad symptom = one question, in the ads'
 * register. Persuasion spine is MMLP051 (T-Upgrade GLP-1 LP): "GLP-1 only does
 * half the job" -> symptom self-admission -> "your GLP-1 is doing its job, and
 * that's the problem" absolution -> "rapid weight loss is starving your
 * testosterone" mechanism. The verdict is the locked T-suppression surface with
 * catabolic-deficit forced Primary (see quizzes/quiz-glp-profile.ts).
 *
 * 9 single-choice screens + 3 interstitials (2 bridges + analyzing) = 12.
 * Design spec: docs/specs/2026-06-08-quiz-glp-quiz-design.md.
 *
 * FIRST quiz with a GLP-1 screener -- glp1-status is now GLP-1-users-only
 * (on-glp1/came-off), so every respondent routes to the glp1-user persona,
 * which no prior quiz could reach. The other five persona branches are
 * intentionally unreachable here.
 */

const QUESTION_ORDER = [
  "age",
  "glp1-status",
  "results-want",
  "arm-shrink",
  "glp1-face",
  "bridge-absolution",
  "body-composition",
  "protein-intake",
  "libido",
  "when-started",
  "bridge-mechanism",
  "analyzing",
] as const;

const questions: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old are you?",
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40–49 years old" },
      { value: "30-39", label: "30–39 years old" },
      { value: "20-29", label: "20–29 years old" },
    ],
  },
  {
    id: "glp1-status",
    type: "single",
    prompt: "Are you currently using a GLP-1 for weight loss?",
    options: [
      { value: "on-glp1", label: "Yes, Ozempic, Wegovy, Mounjaro, Zepbound, or compounded" },
      { value: "came-off", label: "I recently came off one" },
    ],
  },
  {
    id: "results-want",
    type: "single",
    prompt: "When you hit your goal weight, what do you most want to see?",
    options: [
      { value: "sex-drive", label: "My drive and confidence back" },
      { value: "all-of-above", label: "All of it, I want the full transformation" },
    ],
  },
  {
    id: "arm-shrink",
    type: "single",
    prompt: "Since starting GLP-1, have your arms or shoulders started looking smaller or softer?",
    options: [
      { value: "yes-muscle-loss", label: "Yes, I’ve lost muscle mass as well as fat" },
      { value: "yes-noticeably", label: "Yes, clothes hang looser, arms look deflated" },
      { value: "somewhat", label: "A little, I’ve noticed some" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "glp1-face",
    type: "single",
    prompt: "Has your face started looking gaunt, hollow, or older since the weight came off?",
    options: [
      { value: "yes-noticeably", label: "Yes, that drawn, hollow look people call “Ozempic face”" },
      { value: "some", label: "Somewhat, it looks more drawn than it used to" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "bridge-absolution",
    type: "info",
    heading: "It’s not the medication, and it’s not you.",
    body: [
      "You did the hard part. You took action, and the weight is actually coming off.",
      "But losing it this fast puts your body under a kind of pressure that can actually take away hard earned muscle. It can be even harder to get that muscle back once you lose it.",
      "A few more questions and we’ll show you exactly what it’s costing you.",
    ],
    // BRIDGE 1 image (operator-supplied): mechanism-silent by design -- a
    // body-composition comparison (soft, muscle-depleted chest vs lean, muscular
    // chest). No product, no hormone diagram; it shows the cost (lost muscle),
    // not the pathway. Renders below the Continue CTA (rule 20).
    image: {
      src: "/static/images/quiz-glp-absolution.webp",
      alt: "Side-by-side torso comparison showing a soft, muscle-depleted chest next to a lean, muscular chest",
    },
  },
  {
    id: "body-composition",
    type: "single",
    prompt: "How would you describe your body right now?",
    options: [
      { value: "lean-muscular", label: "Lean and muscular" },
      { value: "average", label: "Average build" },
      { value: "soft-some-muscle", label: "Smaller, but soft, or “skinny-fat”" },
      { value: "overweight", label: "Still carrying more weight than I want" },
    ],
  },
  {
    id: "protein-intake",
    type: "single",
    prompt: "Since your appetite dropped, are you eating a lot less, especially protein?",
    options: [
      { value: "way-less", label: "Way less, I can barely finish a meal" },
      { value: "somewhat-less", label: "Somewhat less than I used to" },
      { value: "about-same", label: "About the same" },
      { value: "track-protein", label: "No, I track protein closely" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "When protein drops during fast weight loss, your body breaks down muscle for fuel. That’s how “lighter” quietly becomes “smaller and weaker.”",
    },
  },
  {
    id: "libido",
    type: "single",
    prompt: "Has your sex drive or motivation dropped since you started losing weight?",
    options: [
      { value: "significantly", label: "Significantly" },
      { value: "somewhat", label: "Somewhat" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Testosterone drives libido. When rapid weight loss pulls your T down, drive is usually the first thing to fade.",
    },
  },
  {
    id: "when-started",
    type: "single",
    prompt: "How long have you been noticing these changes?",
    options: [
      { value: "over-year", label: "Over a year ago" },
      { value: "past-year", label: "In the past year" },
      { value: "few-months", label: "In the past few months" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  {
    id: "bridge-mechanism",
    type: "info",
    heading: "Rapid weight loss is starving your testosterone.",
    body: [
      "GLP-1s are powerful at two things: killing your appetite and forcing the weight off fast.",
      "But your body reads rapid weight loss as a crisis. It pulls testosterone down, and without it, what you lose comes from muscle, not just fat. That’s the gaunt face and the deflated arms.",
      "Protect your testosterone and you lose fat without losing muscle. Let’s see how much it’s already costing you.",
    ],
    // BRIDGE 2 image (operator-supplied): the rapid-weight-loss -> testosterone
    // drop -> muscle-mass loss cascade diagram. The operator chose this mechanism
    // flow over the originally-planned first-product shot. Renders below the
    // Continue CTA (rule 20).
    image: {
      src: "/static/images/quiz-glp-mechanism.webp",
      alt: "A cascade diagram: rapid weight loss leads to a testosterone decrease, which leads to muscle mass decrease",
    },
  },
  {
    id: "analyzing",
    type: "info",
    heading: "Analyzing your muscle-loss risk profile…",
    body: [
      "Counting the signs you just reported, and measuring how hard rapid weight loss is working against your testosterone.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const quizGlpQuiz = defineQuiz({
  id: "quiz-glp",
  slug: "quiz-glp",
  meta: {
    title: "What GLP-1 Is Quietly Costing You",
    introHeadline:
      "Is GLP-1 REALLY making you healthier, or just smaller and softer?",
    introBody:
      "See what rapid weight loss is quietly costing you underneath the scale in this 2 minute quiz.",
    testimonial: {
      // Quiz-benefit entry proof (replaces the result-leak register): reveal-safe,
      // GLP-1-flavored, names no product or number. TODO: back the
      // "Verified Customer" tag with a real GLP-1 review before relying on it.
      quote:
        "Lost the weight on my shot but still couldn’t figure out why I looked worse. This quiz finally explained why in about two minutes. So glad I took it.",
      author: "DAVID R., Verified Review",
      avatar: "/static/images/quiz-glp-testimonial-avatar.webp",
    },
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    const next = QUESTION_ORDER[idx + 1];
    return next ?? null;
  },

  classifier(_answers: Answers): PersonaKey {
    // glp1-status is GLP-1-users-only (on-glp1 / came-off), so every respondent
    // is a current or former GLP-1 user -- glp1-user is the only reachable
    // persona. (Before the 2026-06-12 copy pass this quiz also screened
    // considering/no users into the five organic personas via age / body /
    // energy-3pm / onset / libido; those options + the energy-3pm question were
    // removed, making this quiz single-persona by design.) The projection header
    // pulls the glp1-user result copy.
    return "glp1-user";
  },

  deriveProfile: deriveQuizGlpProfile,
});
